import { Link, Navigate, Route, Routes } from "react-router-dom";
import { Home } from "./screens/Home";
import { Feed } from "./screens/Feed";
import { Rooms } from "./screens/Rooms";
import { ViewEditor } from "./screens/ViewEditor";
import { AdoptControlScreen } from "./screens/AdoptControlScreen";
import { Browse } from "./screens/Browse";
import { CollectionDetail } from "./screens/CollectionDetail";
import { Collections, CollectionEdit } from "./screens/Collections";
import { Profile } from "./screens/Profile";
import { Settings } from "./screens/Settings";
import { PublicProfile } from "./screens/PublicProfile";
import { Privacy } from "./screens/Privacy";
import { HowTo } from "./screens/HowTo";
import { Terms } from "./screens/legal/Terms";
import { Dmca } from "./screens/legal/Dmca";
import { Records2257 } from "./screens/legal/Records2257";
import { Contact } from "./screens/legal/Contact";
import { GoldThanks } from "./screens/GoldThanks";
import { WallThanks } from "./screens/WallThanks";
import { Shop } from "./screens/Shop";
import { ShopProduct } from "./screens/ShopProduct";
import { ShopThanks } from "./screens/ShopThanks";
import { ShopCart } from "./screens/ShopCart";
import { ShopCategoryPage } from "./screens/ShopCategory";
import { ShopOrderPage, ShopOrders } from "./screens/ShopOrders";
import { RedeemLink } from "./screens/RedeemLink";
import { GoonFuel } from "./components/GoonFuel";
import { SiteChrome } from "../components/SiteChrome";
import { SecurityQuestionsNudge } from "./components/SecurityQuestionsNudge";
import { SiteFooter } from "./components/SiteFooter";
import { AnalyticsRole } from "../lib/useAnalyticsRole";

/**
 * The control panel. App.tsx mounts this at "/*", so all sub-routes are nested here.
 *   /feed, /feed/:id  -> the Feed: members' posts (one clip each), crowns, comments, boards
 *   /views            -> views and screens: the view library beside the devices it feeds
 *   /views/:id        -> the view editor; :id may be temporary (`t_…`) or saved
 *   /collections      -> collection builder, incl. the auto "Faps" collection (signed-in)
 *   /profile          -> the profile page: gooning stats + share cards (signed-in;
 *                        /stats redirects here)
 *   /settings         -> account settings: email, date of birth, gender, the security
 *                        questions, and the Patreon link (signed-in). NOT /api/settings,
 *                        which is the public system-settings read
 *   /u/:username      -> a lord's PUBLIC page (share-card QR lands here). Renders the
 *                        same body as /profile when their profile switch is public
 *
 * `/` is NOT here. It is the marketing front door, mounted in App.tsx outside this
 * chrome — so the app proper begins at /views. An unknown path stays here and shows
 * a Not Found screen (with a link Home) rather than silently dumping the visitor on
 * the Community Wall.
 *
 * GoonFuel is mounted here (not in App) so the floating player rides along on every
 * control screen but never shows on a display (/view/...).
 */
/**
 * "Skip to content" — the first Tab stop on every chrome page, invisible until focused.
 * 2026-10-05 evening QA (keyboard-only): /browse took ~35 Tabs (site bar, ~30 filter
 * rows, empty ad stops) to reach the sort or a tile. It lands on the page's own
 * `[data-skip-target]` when it nominates one (Browse: the results column), else on the
 * routes' wrapper below the site bar. Focus is moved by hand instead of following the
 * hash, so a skip never pushes a `#main-content` history entry. No hooks: ControlApp's
 * tree is read by calling it (see home-surface.mts), and this is only an element there.
 */
function SkipToContent() {
  return (
    <a
      href="#main-content"
      onClick={(e) => {
        const target =
          document.querySelector<HTMLElement>("[data-skip-target]") ??
          document.getElementById("main-content");
        if (!target) return;
        e.preventDefault();
        // "nearest", not "start": a block-start landing slides the target's top under
        // the sticky site bar. Already on screen means no scroll at all, and the next
        // Tab scrolls its own stop into view.
        target.focus({ preventScroll: true });
        target.scrollIntoView({ block: "nearest" });
      }}
      className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[70] focus:rounded-lg focus:bg-[var(--color-accent)] focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-black"
    >
      Skip to content
    </a>
  );
}

export function ControlApp() {
  return (
    // `dvh`, not `vh`: `min-h-screen` is `100vh`, which on iOS and Chrome Android means
    // the address-bar-hidden viewport, so with the bar showing every page is guaranteed
    // taller than what you can see and ends in a strip of dead scroll.
    <div className="min-h-[100dvh] bg-[var(--color-ink)] text-zinc-200">
      {/* An element, not a hook: home-surface.mts reads this component's tree by calling
          it as a function, which only works while it calls no hooks of its own. */}
      <AnalyticsRole role="control" />
      <SkipToContent />
      {/* CODE-LESS: nothing on these pages plays (the library, the editor, Browse, the
          Feed, Collections, a 404), so none of them is a screen worth claiming, and a
          "Code:" chip here read as "this tab is your screen" when it was not. Code-less
          chrome skips the announce too, not just the chip. The surfaces that DO play — the
          front door's wall, /screen, a device, a minted /play — wire their own code
          through DisplayApp / MintedScreen and are untouched (2026-10-21). */}
      <SiteChrome code={false} />
      {/* The skip link's landing, and the floor that keeps the footer below the fold.
          Every page here paints a short loading state first, so without a minimum the
          footer (and its ad) drew at the top of the screen and was shoved off it the
          moment the data arrived — the biggest single layout shift on Feed and
          Collections. At least a viewport tall minus the bar (2.75rem is under the bar's
          real height, so the footer's first line still starts past the fold). `svh`, the
          address-bar-showing height, never resizes while scrolling, so the floor holds
          still; plain `vh` is the fallback where `svh` is unknown. A short page (a 404)
          keeps its look and only its footer sits lower. */}
      <div
        id="main-content"
        tabIndex={-1}
        className="min-h-[calc(100vh-2.75rem)] scroll-mt-14 outline-none supports-[height:100svh]:min-h-[calc(100svh-2.75rem)]"
      >
      <Routes>
        {/* The library, beside the screens it feeds. Nothing about the surface changed
            when it moved off `/` — only that reaching it is now a deliberate step past
            the front door rather than the thing a first-time visitor is dropped into. */}
        <Route path="/views" element={<Home />} />
        {/* The Feed: posts, crowns, comments, the daily boards. One post has its own address. */}
        <Route path="/feed" element={<Feed />} />
        <Route path="/feed/:id" element={<Feed />} />
        {/* Goon Rooms: the open public rooms and the door to your own. The room itself
            (/room/:id) lives in App.tsx, chrome-free, because it is a screen. */}
        <Route path="/rooms" element={<Rooms />} />
        <Route path="/views/:id" element={<ViewEditor />} />
        {/* `/claim/:code` is NOT here: a scanned party badge needs to land chrome-free,
            so that route lives in App.tsx beside the screen routes (ClaimScreen claims
            the device, then hands off to /remote). */}
        {/* Where a scanned "control from your phone" QR lands: this browser adopts the
            identity the other one was using. */}
        <Route path="/control/:token" element={<AdoptControlScreen />} />
        <Route path="/browse" element={<Browse />} />
        <Route path="/browse/:id" element={<CollectionDetail />} />
        <Route path="/catalog" element={<Navigate to="/browse" replace />} />
        {/* Library was replaced by the auto "Faps" collection — keep old links working. */}
        <Route path="/library" element={<Navigate to="/collections/faps" replace />} />
        <Route path="/collections" element={<Collections />} />
        <Route path="/collections/:id" element={<CollectionEdit />} />
        <Route path="/playlists" element={<Navigate to="/collections" replace />} />
        <Route path="/profile" element={<Profile />} />
        {/* The account's own settings — the four facts sign-up asks for, and where every
            account older than that fills in the blanks. The one prompt toward it is
            SecurityQuestionsNudge (recovery runs on the questions since 2026-10-25). */}
        <Route path="/settings" element={<Settings />} />
        {/* Where the payment processor sends a buyer back to. Inside ControlApp so it
            keeps the site chrome and the session — the page polls the server for the
            grant, which only means anything with a signed-in identity to poll for. */}
        <Route path="/gold/thanks" element={<GoldThanks />} />
        <Route path="/wall/thanks" element={<WallThanks />} />
        {/* The shop. Dark until data/shop.json says otherwise on the server. */}
        <Route path="/shop" element={<Shop />} />
        <Route path="/shop/category/:category" element={<ShopCategoryPage />} />
        <Route path="/shop/cart" element={<ShopCart />} />
        <Route path="/shop/thanks" element={<ShopThanks />} />
        <Route path="/shop/orders" element={<ShopOrders />} />
        <Route path="/shop/orders/:id" element={<ShopOrderPage />} />
        <Route path="/shop/:slug" element={<ShopProduct />} />
        {/* A shop code's delivery link: the wallet's redeem form, prefilled. */}
        <Route path="/faps/redeem" element={<RedeemLink />} />
        {/* The share-card QR's destination: a lord's public page. It renders the same
            body as /profile when the owner's switch is public, minus the card download;
            a private profile shows name + collections only (see PublicProfile.tsx). */}
        <Route path="/u/:username" element={<PublicProfile />} />
        <Route path="/stats" element={<Navigate to="/profile" replace />} />
        {/* The user guide — the same content every surface's ⓘ teaches in place, as a
            linkable page. See HowTo.tsx. */}
        <Route path="/how" element={<HowTo />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/dmca" element={<Dmca />} />
        <Route path="/2257" element={<Records2257 />} />
        <Route path="/contact" element={<Contact />} />
        {/* The legal URLs people guess. 2026-09-25 QA: `/terms-of-service` fell through
            the old splat redirect and landed on the Community Wall, instructional modal
            and all, which reads as the site ignoring the request. */}
        <Route path="/terms-of-service" element={<Navigate to="/terms" replace />} />
        <Route path="/terms-of-use" element={<Navigate to="/terms" replace />} />
        <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
        {/* Anything else is a miss, and says so in the chrome with a link Home. It used to
            be <Navigate to="/">, which made every typo look like the front door. */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </div>
      <SiteFooter />
      {/* After the footer: it floats over the page (see SecurityQuestionsNudge), and its
          spacer has to be the last thing in the flow. Ahead of the footer it left the legal
          links and the 18+ line stuck under the card at the end of every page. */}
      <SecurityQuestionsNudge />
      {/* Desktop only. It is a floating picture-in-picture, and on a phone it sat on top
          of the cards it was floating over — on the one device where the screen is the
          control surface rather than a place to also watch something. */}
      <div className="hidden lg:block">
        <GoonFuel />
      </div>
    </div>
  );
}

/** Unknown control path — same quiet tone as CollectionDetail's miss. Link Home; no auto-redirect. */
function NotFound() {
  return (
    <div className="mx-auto max-w-6xl p-6">
      <p className="py-20 text-center text-sm text-zinc-500">
        Page not found.{" "}
        <Link
          to="/"
          className="text-[var(--color-accent-soft)] hover:underline"
        >
          Home
        </Link>
      </p>
    </div>
  );
}
